import React, { useContext, useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import MaintenanceRequestForm from './MaintenanceRequestForm';
import InfoChip from './InfoChip';
import ProgressStepper from './ProgressStepper';

function TenantMaintenance() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [maintenanceUnread, setMaintenanceUnread] = useState(0);
  const [lastViewedAt, setLastViewedAt] = useState(null);
  const [error, setError] = useState('');
  const [comments, setComments] = useState({});
  const [commentText, setCommentText] = useState({});
  const [commentSending, setCommentSending] = useState({});
  const [hideNewItems, setHideNewItems] = useState(localStorage.getItem('dismiss_newitems_tenant_maintenance') === '1');
  const [maintenanceLocked, setMaintenanceLocked] = useState(false);
  const sectionRefs = {
    updates: useRef(null),
    submit: useRef(null),
    outstanding: useRef(null)
  };
  const [activeSection, setActiveSection] = useState('updates');

  useEffect(() => {
    if (!user || user.userType !== 'tenant') return;
    const fetchRequests = async () => {
      try {
        const [countRes, requestsRes] = await Promise.all([
          fetch(`${process.env.REACT_APP_API_BASE_URL}/api/maintenance/unread-count`, {
            headers: { 'Authorization': `Bearer ${user.token}` }
          }),
          fetch(`${process.env.REACT_APP_API_BASE_URL}/api/tenants/maintenance`, {
            headers: { 'Authorization': `Bearer ${user.token}` }
          })
        ]);

        if (countRes.ok) {
          const countData = await countRes.json().catch(() => ({}));
          setMaintenanceUnread(countData.count || 0);
          setLastViewedAt(countData.last_viewed_at || null);
        }

        if (requestsRes.status === 402) {
          setMaintenanceLocked(true);
          setRequests([]);
          setLoading(false);
          return;
        }
        const data = await requestsRes.json().catch(() => ({}));
        if (!requestsRes.ok) throw new Error(data.error || 'Failed to load maintenance requests');
        setRequests(data.requests || []);

        await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/maintenance/mark-viewed`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${user.token}` }
        });
        window.dispatchEvent(new Event('maintenance:viewed'));
      } catch (err) {
        setError(err.message || 'Could not load maintenance requests.');
      } finally {
        setLoading(false);
      }
    };

    fetchRequests();
  }, [user]);

  useEffect(() => {
    const fetchAllComments = async () => {
      if (!user || !user.token || requests.length === 0) return;
      const entries = await Promise.all(
        requests.map(async (req) => {
          try {
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/maintenance/${req.id}/comments`, {
              headers: { 'Authorization': `Bearer ${user.token}` }
            });
            const data = await res.json().catch(() => ({}));
            return [req.id, res.ok ? (data.comments || []) : []];
          } catch (err) {
            return [req.id, []];
          }
        })
      );
      const sorted = Object.fromEntries(entries.map(([id, list]) => [id, (list || []).sort((a, b) => new Date(b.created_at) - new Date(a.created_at))]));
      setComments(sorted);
    };

    fetchAllComments();
  }, [user, requests]);

  const handleAddComment = async (requestId) => {
    const text = (commentText[requestId] || '').trim();
    if (!text || commentSending[requestId]) return;
    setCommentSending(prev => ({ ...prev, [requestId]: true }));
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/maintenance/${requestId}/comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${user.token}` },
        body: JSON.stringify({ comment_text: text })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to send comment');
      setComments(prev => ({
        ...prev,
        [requestId]: [data.comment, ...(prev[requestId] || [])]
      }));
      setCommentText(prev => ({ ...prev, [requestId]: '' }));
    } catch (err) {
      setError(err.message || 'Could not send comment.');
    } finally {
      setCommentSending(prev => ({ ...prev, [requestId]: false }));
    }
  };

  if (!user || user.userType !== 'tenant') {
    navigate('/login');
    return null;
  }

  const getLastActivity = (req) => {
    const requestTime = req.updated_at || req.created_at;
    const commentTime = (comments[req.id] || [])[0]?.created_at;
    if (requestTime && commentTime) {
      return new Date(requestTime) > new Date(commentTime) ? requestTime : commentTime;
    }
    return requestTime || commentTime || null;
  };

  const latestRequests = [...requests]
    .sort((a, b) => new Date(getLastActivity(b) || 0) - new Date(getLastActivity(a) || 0))
    .slice(0, 3);

  const openRequests = requests.filter((request) => request.status !== 'resolved' && request.status !== 'closed');
  const stepperSteps = [
    {
      key: 'updates',
      label: 'Updates',
      status: (!hideNewItems && maintenanceUnread > 0) ? 'needs_info' : 'approved'
    },
    { key: 'submit', label: 'Submit', status: requests.length ? 'approved' : 'pending' },
    { key: 'outstanding', label: 'Outstanding', status: openRequests.length ? 'needs_info' : 'approved' }
  ];
  const stepperProgress = stepperSteps.filter((step) => step.status === 'approved').length / stepperSteps.length;

  const handleSectionSelect = (key) => {
    setActiveSection(key);
    const ref = sectionRefs[key]?.current;
    if (ref) {
      ref.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <main className="App-main tenants-page">
      <div className="ledger-header">
        <div>
          <p className="eyebrow-tag">Maintenance</p>
          <h2>Maintenance {maintenanceUnread > 0 && <span className="nav-badge">{maintenanceUnread}</span>}</h2>
          <p>Submit and track maintenance requests.</p>
          <InfoChip label="Request tips">
            Include photos + access notes so the landlord can respond faster. You’ll get notified when they reply.
          </InfoChip>
        </div>
      </div>

      <ProgressStepper
        steps={stepperSteps}
        currentKey={activeSection}
        onSelect={handleSectionSelect}
        progress={stepperProgress}
      />

      {!hideNewItems && latestRequests.length > 0 && (
        <div ref={sectionRefs.updates} className="dashboard-section new-items-card" style={{ borderLeft: '4px solid #2f6fed', background: '#f5f8ff' }}>
          <div className="section-header">
            <h3>New items</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span className="report-chip report-chip--muted">Latest activity</span>
              <button
                className="card-dismiss"
                onClick={() => { localStorage.setItem('dismiss_newitems_tenant_maintenance', '1'); setHideNewItems(true); }}
                aria-label="Dismiss"
              >
                Dismiss
              </button>
            </div>
          </div>
        {latestRequests.length === 0 ? (
          <p className="status-text">No maintenance updates yet.</p>
        ) : (
          <div className="inquiry-list">
            {latestRequests.map((req) => {
              const lastActivity = getLastActivity(req);
              const isNew = lastViewedAt && lastActivity && new Date(lastActivity) > new Date(lastViewedAt);
              return (
                <div key={`latest-${req.id}`} className="inquiry-card">
                  <p><strong>{req.title}</strong></p>
                  <p className="status-text">Status: {req.status}</p>
                  <p className="timestamp">{lastActivity ? new Date(lastActivity).toLocaleString() : '—'}</p>
                  <div className="form-actions" style={{ marginTop: '0.5rem' }}>
                    <a className="secondary-button" href={`#request-${req.id}`}>View request</a>
                    {isNew && <span className="report-chip" style={{ marginLeft: '0.5rem' }}>New</span>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      )}

      <div ref={sectionRefs.submit} className="dashboard-section">
        {maintenanceLocked ? (
          <p className="status-text">Maintenance is a Pro feature. Ask your landlord to upgrade to submit requests.</p>
        ) : (
          <>
            <InfoChip label="What to include">
              Mention access times and attach photos/videos so maintenance knows what to bring.
            </InfoChip>
            <MaintenanceRequestForm />
          </>
        )}
      </div>

      <div ref={sectionRefs.outstanding} className="dashboard-section">
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
          <h3 style={{ margin: 0 }}>Outstanding Requests</h3>
          <InfoChip label="Updates">
            Reply in the thread to keep everything in one place; you’ll see the most recent message on top.
          </InfoChip>
        </div>
        {maintenanceLocked ? (
          <p className="status-text">Maintenance is a Pro feature. Ask your landlord to upgrade to view requests.</p>
        ) : loading ? (
          <p className="status-text">Loading requests...</p>
        ) : error ? (
          <p className="error-message">{error}</p>
        ) : requests.filter(req => req.status !== 'resolved').length === 0 ? (
          <p className="status-text">No outstanding requests.</p>
        ) : (
          <div className="application-list">
            {requests.filter(req => req.status !== 'resolved').map((req) => (
              <div key={req.id} id={`request-${req.id}`} className="application-card">
                <p><strong>{req.title}</strong></p>
                {req.description && <p>{req.description}</p>}
                <p className="status-text">Status: {req.status}</p>
                <p className="timestamp">Submitted on: {new Date(req.created_at).toLocaleDateString()}</p>
                <div className="lease-chat-thread" style={{ marginTop: '0.75rem' }}>
                  {(comments[req.id] || []).length === 0 ? (
                    <p className="status-text">No comments yet.</p>
                  ) : (
                    (comments[req.id] || []).map((comment) => (
                      <div key={comment.id} className={`lease-chat-message ${comment.user_type}`}>
                        <p>{comment.comment_text}</p>
                        <span>{comment.user_type === 'landlord' ? 'Owner' : 'Tenant'}</span>
                      </div>
                    ))
                  )}
                </div>
                <div className="lease-chat-compose">
                  <textarea
                    className="comment-box"
                    rows="2"
                    value={commentText[req.id] || ''}
                    onChange={(e) => setCommentText(prev => ({ ...prev, [req.id]: e.target.value }))}
                    placeholder="Add a comment..."
                  />
                  <button className="primary-button" onClick={() => handleAddComment(req.id)} disabled={commentSending[req.id] || !(commentText[req.id] || '').trim()}>
                    {commentSending[req.id] ? 'Sending...' : 'Send'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="dashboard-section">
        <h3>Completed Requests</h3>
        {loading ? (
          <p className="status-text">Loading requests...</p>
        ) : error ? (
          <p className="error-message">{error}</p>
        ) : requests.filter(req => req.status === 'resolved').length === 0 ? (
          <p className="status-text">No completed requests yet.</p>
        ) : (
          <div className="application-list">
            {requests.filter(req => req.status === 'resolved').map((req) => (
              <div key={req.id} id={`request-${req.id}`} className="application-card">
                <p><strong>{req.title}</strong></p>
                {req.description && <p>{req.description}</p>}
                <p className="status-text">Status: {req.status}</p>
                <p className="timestamp">Resolved on: {req.updated_at ? new Date(req.updated_at).toLocaleDateString() : '—'}</p>
                <div className="lease-chat-thread" style={{ marginTop: '0.75rem' }}>
                  {(comments[req.id] || []).length === 0 ? (
                    <p className="status-text">No comments yet.</p>
                  ) : (
                    (comments[req.id] || []).map((comment) => (
                      <div key={comment.id} className={`lease-chat-message ${comment.user_type}`}>
                        <p>{comment.comment_text}</p>
                        <span>{comment.user_type === 'landlord' ? 'Owner' : 'Tenant'}</span>
                      </div>
                    ))
                  )}
                </div>
                <div className="lease-chat-compose">
                  <textarea
                    className="comment-box"
                    rows="2"
                    value={commentText[req.id] || ''}
                    onChange={(e) => setCommentText(prev => ({ ...prev, [req.id]: e.target.value }))}
                    placeholder="Add a comment..."
                  />
                  <button className="primary-button" onClick={() => handleAddComment(req.id)} disabled={commentSending[req.id] || !(commentText[req.id] || '').trim()}>
                    {commentSending[req.id] ? 'Sending...' : 'Send'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </main>
  );
}

export default TenantMaintenance;
